/*
 *   File : dashboard.js
 *   Author : https://evoqins.com
 *   Description : dashboard component
 *   Integrations : dashboard/filter-data, dashboard/cards, dashboard/graph, dashboard/top-investments
 *   Version : 2.1.0
 */
import React, { useEffect, useState } from "react";
import { SummaryCard } from "../../Components/Cards";
import { LineChart } from "../../Components/Charts";
import { Tabs } from "../../Components/Navigation";
import { BasicFilter, CustomDatePicker } from "../../Components/Filters";
import { DataTableComponent } from "../../Components/DataTable";
import { useDispatch } from "react-redux";
import { setPageTitle, setSearchEnabled } from "../../Redux/Ui/ui.slice";
import { APIService } from "../../Service";
import { Loader, EmptyScreen } from "../../Components/Other";
import { Link } from "react-router-dom";

const TAB_DATA = [
  { id: 0, label: "usersGrowth", name: "Users Growth" },
  { id: 1, label: "aumGrowth", name: "AUM Growth" },
  { id: 2, label: "goalsCreated", name: "No Of Goals Created" },
  { id: 3, label: "usersInvested", name: "Users Invested In funds" },
];

// Graph tab id → API response key
const TAB_GRAPH_KEY_MAP = {
  0: "users_growth",
  1: "aum_growth",
  2: "goals_created",
  3: "users_invested_in_funds",
};

const COLUMNS = [
  {
    name: "User ID",
    selector: (row) => row.customer_id,
    width: "150px",
  },
  {
    name: "User Name",
    selector: (row) => row.name,
    wrap: true,
    width: "200px",
    cell: (row) => (
      <div className="d-flex column-gap-2 align-items-center">
        <img
          src={require("../../Assets/Images/avatar.jpg")}
          alt={row.name}
          style={{
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            objectFit: "cover",
          }}
        />
        <span>{row.name}</span>
      </div>
    ),
  },
  {
    name: "Email",
    selector: (row) => row.email,
    wrap: true,
    width: "250px",
  },
  {
    name: "Mobile Number",
    selector: (row) => row.mobile,
    width: "200px",
  },
  {
    name: "Joined On",
    selector: (row) => row.joined_on,
    width: "200px",
  },
  {
    name: "AUM",
    selector: (row) => row.aum_text,
    width: "150px",
  },
];

// Helper: build SummaryCard array from API metrics block
function _buildSummaryCards(metrics) {
  if (!metrics) return [];

  const fmt = (val, pct, fmtVal, url) => ({
    description: fmtVal ?? String(val ?? "—"),
    value: `${(pct ?? 0) >= 0 ? "+" : ""}${pct ?? 0}%`,
    icon: (pct ?? 0) >= 0 ? "upward-swiggle" : "downward-swiggle",
    redirection_url: url || null,
  });

  return [
    {
      title: "Total Users",
      ...fmt(
        metrics.total_users?.value,
        metrics.total_users?.percentage,
        undefined,
        metrics.total_users?.redirection_url,
      ),
    },
    {
      title: "Invested Users",
      ...fmt(
        metrics.invested_users?.value,
        metrics.invested_users?.percentage,
        undefined,
        metrics.invested_users?.redirection_url,
      ),
    },
    {
      title: "Non Invested Users",
      ...fmt(
        metrics.non_invested_users?.value,
        metrics.non_invested_users?.percentage,
        undefined,
        metrics.non_invested_users?.redirection_url,
      ),
    },
    {
      title: "Total AUM",
      ...fmt(
        metrics.total_aum?.value,
        metrics.total_aum?.percentage,
        metrics.total_aum?.formatted_value,
        metrics.total_aum?.redirection_url,
      ),
    },
    {
      title: "AUM in Funds",
      ...fmt(
        metrics.aum_in_funds?.value,
        metrics.aum_in_funds?.percentage,
        metrics.aum_in_funds?.formatted_value,
        metrics.aum_in_funds?.redirection_url,
      ),
    },
    {
      title: "AUM In Goals",
      ...fmt(
        metrics.aum_in_goals?.value,
        metrics.aum_in_goals?.percentage,
        metrics.aum_in_goals?.formatted_value,
        metrics.aum_in_goals?.redirection_url,
      ),
    },
    {
      title: "Number Of Transactions",
      ...fmt(
        metrics.transactions?.value,
        metrics.transactions?.percentage,
        undefined,
        metrics.transactions?.redirection_url,
      ),
    },
    {
      title: "Pending Transactions",
      ...fmt(
        metrics.pending_transactions?.value,
        metrics.pending_transactions?.percentage,
        undefined,
        metrics.pending_transactions?.redirection_url,
      ),
    },
  ];
}

export default function Dashboard() {
  const dispatch = useDispatch();

  // Shared filter options (same list, used in both dropdowns independently)
  const [filterOptions, setFilterOptions] = useState([]);
  const [dateRange, setDateRange] = useState();

  // ── Summary cards ─────────────────────────────────────────────────────────
  const [summaryCard, setSummaryCard] = useState([]);
  const [cardsLoader, setCardsLoader] = useState(true);
  const [cardsEmpty, setCardsEmpty] = useState(false);

  // ── Graph — own filter state ───────────────────────────────────────────────
  const [graphMonths, setGraphMonths] = useState(6);
  const [activeTab, setActiveTab] = useState(0);
  const [allGraphData, setAllGraphData] = useState(null);
  const [chartData, setChartData] = useState({});
  const [graphLoader, setGraphLoader] = useState(true);
  const [graphEmpty, setGraphEmpty] = useState(false);

  // ── Table — own filter state ───────────────────────────────────────────────
  const [tableMonths, setTableMonths] = useState(6);
  const [tableData, setTableData] = useState([]);
  const [tableLoader, setTableLoader] = useState(true);
  const [tableEmpty, setTableEmpty] = useState(false);

  // Redux page title / search bar
  useEffect(() => {
    dispatch(setPageTitle(["Dashboard"]));
    dispatch(setSearchEnabled(false));
  }, []);

  // On mount: load filter options and summary cards (cards have no filter)
  useEffect(() => {
    _getFilterData();
    _getCards();
  }, []);

  // Graph refetches only when datePicker  changes
  useEffect(() => {
    _getCards();
  }, [dateRange]);

  // Graph refetches only when graphMonths changes
  useEffect(() => {
    _getGraph();
  }, [graphMonths]);

  // Table refetches only when tableMonths changes
  useEffect(() => {
    _getTopInvestments();
  }, [tableMonths]);

  // Tab switch: re-slice cached graph data — no API call needed
  useEffect(() => {
    if (!allGraphData) return;
    _deriveChartData(allGraphData, activeTab);
  }, [activeTab, allGraphData]);

  // ─── API: dashboard/filter-data (GET) ─────────────────────────────────────
  const _getFilterData = () => {
    APIService(false, "dashboard/filter-data")
      .then((res) => {
        if (res?.status_code === 200 && Array.isArray(res.data)) {
          setFilterOptions(
            res.data.map((item) => ({
              label: item.label,
              value: String(item.value),
            })),
          );
        }
      })
      .catch((err) => console.error("Failed to load filter data:", err));
  };

  // ─── API: dashboard/cards ─────────────────────────────────────────────────
  const _getCards = () => {
    setCardsLoader(true);
    setCardsEmpty(false);
    APIService(true, "dashboard/cards", {
      ...dateRange,
    })
      .then((res) => {
        if (res?.status_code === 200 && res.data?.metrics) {
          const cards = _buildSummaryCards(res.data.metrics);
          setSummaryCard(cards);
          setCardsEmpty(cards.length === 0);
        } else {
          setSummaryCard([]);
          setCardsEmpty(true);
        }
      })
      .catch((err) => {
        console.error("Failed to load dashboard cards:", err);
        setSummaryCard([]);
        setCardsEmpty(true);
      })
      .finally(() => setCardsLoader(false));
  };

  // ─── API: dashboard/graph ─────────────────────────────────────────────────
  const _getGraph = () => {
    setGraphLoader(true);
    setGraphEmpty(false);

    APIService(true, "dashboard/graph", { months: graphMonths })
      .then((res) => {
        if (res?.status_code === 200 && res.data?.graphs) {
          setAllGraphData(res.data.graphs);
          _deriveChartData(res.data.graphs, activeTab);
        } else {
          setChartData({});
          setGraphEmpty(true);
        }
      })
      .catch((err) => {
        console.error("Failed to load dashboard graph:", err);
        setChartData({});
        setGraphEmpty(true);
      })
      .finally(() => setGraphLoader(false));
  };

  // Helper: pick the correct series for the active tab from the full graphs payload
  const _deriveChartData = (graphs, tabId) => {
    const key = TAB_GRAPH_KEY_MAP[tabId] ?? "users_growth";
    const section = graphs[key];
    if (section?.data && section?.label) {
      setChartData({ labels: section.label, dataSet: section.data });
      setGraphEmpty(false);
    } else {
      setChartData({});
      setGraphEmpty(true);
    }
  };

  // ─── API: dashboard/top-investments ──────────────────────────────────────
  const _getTopInvestments = () => {
    setTableLoader(true);
    setTableEmpty(false);

    APIService(true, "dashboard/top-investments", { months: tableMonths })
      .then((res) => {
        if (res?.status_code === 200 && Array.isArray(res.data?.users)) {
          setTableData(res.data.users);
          setTableEmpty(res.data.users.length === 0);
        } else {
          setTableData([]);
          setTableEmpty(true);
        }
      })
      .catch((err) => {
        console.error("Failed to load top investments:", err);
        setTableData([]);
        setTableEmpty(true);
      })
      .finally(() => setTableLoader(false));
  };

  return (
    <div className="pt-48 d-flex flex-column">
      <div className="row">
        <div className="col-12 d-flex flex-column rg-24">
          {/* ── Summary Cards (no filter) ── */}
          <div
            className="e-glass-card rounded-16 p-24 bg-transparent position-relative"
            style={{ zIndex: 5 }}
          >
            <div className="d-flex  justify-content-between align-items-center w-100 pb-3">
              <h3 className="mb-0 fw-500 fs-14 lh-20 text-black ">Overview</h3>
              <CustomDatePicker
                key={"resetKey"} // increment resetKey to force remount
                value={dateRange}
                onChange={({ from_date, till_date }) => {
                  setDateRange({ from_date, till_date });
                }}
                placeholder="All Time"
              />
            </div>
            {cardsLoader ? (
              <Loader />
            ) : cardsEmpty ? (
              <EmptyScreen message="No Overview Data Found" />
            ) : (
              <div className="row row-cols-lg-4 row-cols-md-3 row-cols-2 rg-24">
                {summaryCard.map((card, i) => {
                  const content = (
                    <SummaryCard
                      title={card.title}
                      description={card.description}
                      value={card.value}
                      icon={card.icon}
                    />
                  );

                  return (
                    <div key={i} className="col">
                      {card?.redirection_url ? (
                        <Link
                          to={`/${card.redirection_url}`}
                          className="text-decoration-none"
                        >
                          {content}
                        </Link>
                      ) : (
                        content
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Graph (graph filter → only refetches graph) ── */}
          <div className="e-glass-card rounded-16 d-flex flex-column rg-24 p-24 bg-transparent">
            <div className="d-flex justify-content-between align-items-center">
              <Tabs
                data={TAB_DATA}
                defaultActive={0}
                onChange={(activeId) => setActiveTab(activeId)}
              />
              <BasicFilter
                options={filterOptions}
                defaultActive={"6"}
                onChange={(data) => setGraphMonths(Number(data?.value ?? 6))}
              />
            </div>
            {graphLoader ? (
              <Loader />
            ) : graphEmpty ? (
              <EmptyScreen message="No Graph Data Found" />
            ) : (
              <LineChart
                labels={chartData.labels}
                datasets={[{ data: chartData.dataSet, borderColor: "#1877F2" }]}
              />
            )}
          </div>

          {/* ── Top Investments Table (table filter → only refetches table) ── */}
          <div className="e-glass-card rounded-16 d-flex flex-column rg-24 bg-transparent">
            <div className="d-flex justify-content-between align-items-center px-24 pt-24">
              <h3 className="mb-0 fw-500 fs-18 lh-20 text-eerie-black">
                Top 10 Users With Highest Investments
              </h3>
              <BasicFilter
                options={filterOptions}
                defaultActive={"6"}
                onChange={(data) => setTableMonths(Number(data?.value ?? 6))}
              />
            </div>
            {tableLoader ? (
              <Loader />
            ) : tableEmpty ? (
              <EmptyScreen message="No Investment Data Found" />
            ) : (
              <DataTableComponent
                info={false}
                columns={COLUMNS}
                data={tableData}
                pagination={false}
                className={"e-table-basic"}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
